import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  BreadcrumbArray,
  Button,
  DataTable,
  DataTableColumnHeader,
  Number as NumberUI,
  PrimaryFullScreenSpinner,
  Txt,
  toast,
} from "@repo/ui";
import {
  brandsByKey,
  createLogger,
  formatRate,
  NotFoundError,
} from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import Decimal from "decimal.js";
import { Suspense, useRef, useState } from "react";
import { PiCheckCircle } from "react-icons/pi";
import { InlineSaveStatus } from "~/components/InlineSaveStatus";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import type {
  YieldHubProductFragment,
  YieldHubRateFragment,
} from "~/gql/client/graphql";
import { opsHead } from "~/head";

const logger = createLogger({ name: "ops.yieldhubSync" });

export const Route = createFileRoute("/__auth/yieldhub-sync_/$fileId")({
  head: () => opsHead("Yieldhub Sync"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

type RateRow = {
  product: YieldHubProductFragment;
  newRate?: YieldHubRateFragment | null;
};

function isRateChanged(row: RateRow): boolean {
  if (!row.newRate) {
    return false;
  }
  if (!row.product.apy) {
    return true;
  }
  return !new Decimal(row.newRate.rate).equals(row.product.apy);
}

function View() {
  const { fileId } = Route.useParams();
  const savingProductIdRef = useRef<string | null>(null);
  const [savedProductIds, setSavedProductIds] = useState(
    () => new Set<string>(),
  );

  graphql(`
    fragment YieldHubProduct on TermDepositProduct {
      id
      internalName
      provider {
        id
        name
      }
      liquidityInDays
      apy
    }

    fragment YieldHubRate on TermDepositRateQuote {
      id
      duration
      rate
      effectiveAt
      minBalanceInclusive
      maxBalanceExclusive
    }
  `);

  const { data } = useSuspenseQuery(
    graphql(`
      query YieldHubFile($id: ID!) {
        yieldHubFile(id: $id) {
          id
          productRates {
            product {
              ...YieldHubProduct
            }
            newRate {
              ...YieldHubRate
            }
          }
        }
      }
    `),
    { variables: { id: fileId } },
  );

  const file = data.yieldHubFile;
  if (!file) {
    throw new NotFoundError();
  }

  const rows: RateRow[] = [...file.productRates].sort((a, b) => {
    const aProviderName = a.product.provider
      ? (brandsByKey[a.product.provider.name]?.displayName ??
        a.product.provider.name)
      : "";
    const bProviderName = b.product.provider
      ? (brandsByKey[b.product.provider.name]?.displayName ??
        b.product.provider.name)
      : "";
    const providerCmp = aProviderName.localeCompare(bProviderName);
    if (providerCmp !== 0) {
      return providerCmp;
    }
    return (a.product.liquidityInDays ?? 0) - (b.product.liquidityInDays ?? 0);
  });

  const [createTermDepositRateQuotes, mutationResult] = useMutation(
    graphql(`
      mutation YieldHubCreateTermDepositRateQuotes(
        $input: CreateTermDepositRateQuotesInput!
      ) {
        createTermDepositRateQuotes(input: $input) {
          termDepositProduct {
            id
            apy
            rateQuotes {
              id
              rate
            }
          }
        }
      }
    `),
  );

  const saveRow = async (row: RateRow) => {
    if (!row.newRate) {
      return;
    }
    await createTermDepositRateQuotes({
      variables: {
        input: {
          productId: row.product.id,
          rateQuotes: [
            {
              duration: row.newRate.duration,
              rate: row.newRate.rate,
              effectiveAt: row.newRate.effectiveAt,
              minBalanceInclusive: row.newRate.minBalanceInclusive,
              maxBalanceExclusive: row.newRate.maxBalanceExclusive,
            },
          ],
        },
      },
    });
  };

  const handleSaveRow = async (row: RateRow) => {
    if (mutationResult.loading) {
      return;
    }
    savingProductIdRef.current = row.product.id;
    try {
      await saveRow(row);
      setSavedProductIds((prev) => new Set(prev).add(row.product.id));
      toast({ title: "Rate saved", kind: "success", autoHide: true });
    } catch (error) {
      logger.error({ error }, "Failed to save rate");
      toast({ title: "Failed to save rate", kind: "error", autoHide: false });
    }
  };

  const changedRows = rows.filter(isRateChanged);

  const handleSaveAll = async () => {
    if (mutationResult.loading) {
      return;
    }
    try {
      for (const row of changedRows) {
        savingProductIdRef.current = row.product.id;
        await saveRow(row);
        setSavedProductIds((prev) => new Set(prev).add(row.product.id));
      }
      toast({ title: "Rates saved", kind: "success", autoHide: true });
    } catch {
      toast({ title: "Failed to save rates", kind: "error", autoHide: false });
    }
  };

  const anySaving = mutationResult.loading;

  const columns: ColumnDef<RateRow>[] = [
    {
      accessorKey: "provider",
      header: "Provider",
      cell: ({ row }) => {
        if (!row.original.product.provider) {
          return "-";
        }
        const bankDisplay = brandsByKey[row.original.product.provider.name];
        return bankDisplay?.displayName ?? row.original.product.provider.name;
      },
    },
    {
      id: "id",
      accessorFn: (row) => row.product.id,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Product" />
      ),
      cell: ({ row }) => row.original.product.internalName,
    },
    {
      id: "currentRate",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Current rate" />
      ),
      cell: ({ row }) =>
        row.original.product.apy ? (
          <>{formatRate(row.original.product.apy)}</>
        ) : (
          <>—</>
        ),
    },
    {
      id: "newRate",
      accessorFn: (row) => row.newRate?.rate ?? null,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="New rate" />
      ),
      cell: ({ row }) =>
        row.original.newRate ? (
          <>{formatRate(row.original.newRate.rate)}</>
        ) : (
          <Txt tone="tertiary">Not found</Txt>
        ),
    },
    {
      id: "compare",
      accessorFn: (row) => row.newRate?.rate ?? null,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Compare" />
      ),
      cell: ({ row }) => {
        if (!row.original.newRate) {
          return <>—</>;
        }

        if (savedProductIds.has(row.original.product.id)) {
          return (
            <div className="flex items-center gap-1 text-success">
              <PiCheckCircle className="size-4" />
              <Txt tone="success">Updated</Txt>
            </div>
          );
        }

        const currentRate = row.original.product.apy;
        const newRate = row.original.newRate.rate;
        if (!currentRate || !newRate) {
          return <>—</>;
        }

        const diff = new Decimal(newRate).minus(currentRate);

        if (diff.isZero()) {
          return <Txt tone="default">No change</Txt>;
        }

        const tone = diff.isPositive() ? "success" : "danger";
        return (
          <Txt tone={tone}>
            <NumberUI
              value={diff.toNumber() / 100}
              format={{ ...NumberUI.format.apy(), signDisplay: "exceptZero" }}
            />
          </Txt>
        );
      },
    },
    {
      id: "actions",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Actions" />
      ),
      cell: ({ row }) => {
        if (!isRateChanged(row.original)) {
          // keep rows the same height
          return <div className="h-8"></div>;
        }
        const isThisRowSaving =
          savingProductIdRef.current === row.original.product.id;
        return (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void handleSaveRow(row.original)}
            >
              Save
            </Button>
            {isThisRowSaving ? (
              <InlineSaveStatus mutationResult={mutationResult} />
            ) : (
              <div className="size-4"></div>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <AdminPageContainer>
      <BreadcrumbArray
        stack={[
          { to: "/yieldhub-sync", title: "Sync Yieldhub" },
          { title: "Review rates" },
        ]}
      />
      <AdminPageHeader
        title="Yieldhub rate sync"
        subtitle="Review extracted rates from the HTML, then save to apply them."
        buttons={
          <Button
            variant="primary"
            disabled={changedRows.length === 0}
            onClick={() => void handleSaveAll()}
          >
            {anySaving ? "Saving…" : `Save ${changedRows.length} rates`}
          </Button>
        }
      />
      <DataTable
        data={rows}
        columns={columns}
        className="overflow-hidden border"
        manualSorting={false}
        activeRowId={""}
        manualPagination={true}
        hidePagination={true}
        showCsvExport={false}
        pageSize={10_000}
      />
    </AdminPageContainer>
  );
}
